import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Upload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  MyLocation as MyLocationIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { previewExcel, confirmExcelImport, sniffExcel } from '../../api/reporting';
import { useTenant } from '../../contexts/TenantContext';
import ColumnMapper from './ColumnMapper';

// Accepts both .xls (legacy BIFF) and .xlsx (Office Open XML). The trailing
// `x` is optional so "Report 202412.xls" passes too.
const FILENAME_PATTERN = /\d{6}\.xlsx?$/i;

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getFdiChipProps(record) {
  if (!record.is_valid) {
    return { label: 'Parse Error', color: 'warning', variant: 'outlined' };
  }
  const wp = record.weather_preview;
  if (!wp) return { label: '—', color: 'default' };
  if (wp.fdi_fetch_error) return { label: 'No Weather', color: 'default', variant: 'outlined' };
  const fdi = wp.peak_fdi;
  if (fdi === null || fdi === undefined) return { label: '—', color: 'default' };
  if (fdi <= 20) return { label: `FDI ${fdi}`, color: 'success' };
  if (fdi <= 45) return { label: `FDI ${fdi}`, color: 'success' };
  if (fdi <= 60) return { label: `FDI ${fdi}`, color: 'warning' };
  return { label: `FDI ${fdi}`, color: 'error' };
}

function scopedRowRef(filename, rowRef) {
  return `${filename}::${rowRef}`;
}

// Pull the suggested mapping for a file (uses the first sheet — sheets in a
// workbook share a layout). Returns a plain {fieldKey: columnLetter} object.
function initialMappingFor(sniff) {
  const first = sniff?.sheets?.[0];
  return { ...(first?.suggested_mapping || {}) };
}

export default function ExcelImport() {
  const { currentTenant } = useTenant();

  // ── Wizard step ─────────────────────────────────────────────────────────
  // 'select' → 'mapping' → 'preview' → 'result'
  const [step, setStep] = useState('select');

  // ── Step 1 inputs ───────────────────────────────────────────────────────
  const [files, setFiles] = useState([]); // File[]
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [coordsAutoFilled, setCoordsAutoFilled] = useState(false);
  const [latError, setLatError] = useState('');
  const [lonError, setLonError] = useState('');
  const [filenameErrors, setFilenameErrors] = useState({});

  // ── Sniff + mapping state (Step 2) ──────────────────────────────────────
  // sniffs: filename -> SniffResult
  // mappings: filename -> {fieldKey: columnLetter}
  const [sniffs, setSniffs] = useState({});
  const [mappings, setMappings] = useState({});
  const [isSniffing, setIsSniffing] = useState(false);

  // ── Preview/Confirm state (Steps 3 + 4) ─────────────────────────────────
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  // Skip-weather toggle (mapping step). When set, the preview call asks the
  // backend to skip the Open-Meteo lookup entirely — useful when network is
  // slow, or when the user just wants to confirm the mapping is right
  // before committing to the full preview.
  const [skipWeather, setSkipWeather] = useState(false);
  // When the first weather-enabled attempt times out, we surface a banner
  // letting the user retry without weather instead of just failing.
  const [weatherTimeoutHint, setWeatherTimeoutHint] = useState(false);
  // Per-file progress while we process the upload list serially (1/N → N/N).
  // Holds {current, total, filename} during the preview pass; null otherwise.
  const [previewProgress, setPreviewProgress] = useState(null);

  // ── FPA coord defaulting ────────────────────────────────────────────────

  const fpaLat = currentTenant?.latitude;
  const fpaLon = currentTenant?.longitude;
  const fpaName = currentTenant?.name;

  useEffect(() => {
    if (fpaLat == null || fpaLon == null) return;
    if (!lat && !lon) {
      setLat(String(fpaLat));
      setLon(String(fpaLon));
      setLatError('');
      setLonError('');
      setCoordsAutoFilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fpaLat, fpaLon]);

  const handleUseFpaCoords = () => {
    if (fpaLat == null || fpaLon == null) return;
    setLat(String(fpaLat));
    setLon(String(fpaLon));
    setLatError('');
    setLonError('');
    setCoordsAutoFilled(true);
  };

  // ── Validation ──────────────────────────────────────────────────────────

  const validateFiles = (incoming) => {
    const errs = {};
    for (const f of incoming) {
      if (!FILENAME_PATTERN.test(f.name)) {
        errs[f.name] =
          'Filename must end with YYYYMM.xls or YYYYMM.xlsx (e.g. "Report 202412.xlsx")';
      }
    }
    setFilenameErrors(errs);
  };

  const validateLat = (v) => {
    const n = parseFloat(v);
    if (isNaN(n) || n < -90 || n > 90) setLatError('Latitude must be between -90 and 90');
    else setLatError('');
  };
  const validateLon = (v) => {
    const n = parseFloat(v);
    if (isNaN(n) || n < -180 || n > 180) setLonError('Longitude must be between -180 and 180');
    else setLonError('');
  };

  const allFilenamesValid = useMemo(
    () => files.length > 0 && Object.keys(filenameErrors).length === 0,
    [files, filenameErrors],
  );

  // Records ultimately get persisted under `currentTenant.id`. The import
  // can't proceed without one — see handleConfirm for the same guard
  // server-side. We surface it at step 1 so the user doesn't sit through
  // the (possibly slow) preview only to fail at the final click.
  const hasTenant = Boolean(currentTenant?.id);

  const isReadyToSniff =
    allFilenamesValid && lat && !latError && lon && !lonError && !isSniffing && hasTenant;

  const allMappingsValid = useMemo(() => {
    if (files.length === 0) return false;
    for (const f of files) {
      const m = mappings[f.name];
      // Require at least the date column to be mapped — without a date we
      // can't import the row at all.
      if (!m || !m.date_raw) return false;
    }
    return true;
  }, [files, mappings]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const next = Array.from(e.target.files || []);
    setFiles(next);
    validateFiles(next);
  };

  const removeFile = (name) => {
    const next = files.filter((f) => f.name !== name);
    setFiles(next);
    validateFiles(next);
  };

  const handleLatChange = (e) => {
    setLat(e.target.value);
    setCoordsAutoFilled(false);
    validateLat(e.target.value);
  };
  const handleLonChange = (e) => {
    setLon(e.target.value);
    setCoordsAutoFilled(false);
    validateLon(e.target.value);
  };

  // ── Step 1 → Step 2: sniff every file in parallel ───────────────────────

  const handleSniff = async () => {
    setIsSniffing(true);
    setError(null);
    // Sniff is fast (no external calls) but we still process serially so
    // that one bad file's error is surfaced as "file 3/8: <name>" rather
    // than buried in a Promise.all rejection.
    try {
      const sniffMap = {};
      const mapMap = {};
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        setPreviewProgress({ current: i + 1, total: files.length, filename: f.name });
        const formData = new FormData();
        formData.append('file', f);
        const data = await sniffExcel(formData);
        sniffMap[f.name] = data;
        mapMap[f.name] = initialMappingFor(data);
      }
      setSniffs(sniffMap);
      setMappings(mapMap);
      setStep('mapping');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        setError(`Sniff errors: ${detail.errors.join('; ')}`);
      } else {
        setError(detail || 'Failed to read Excel file(s). Check format and try again.');
      }
    } finally {
      setIsSniffing(false);
      setPreviewProgress(null);
    }
  };

  const handleMappingChange = (filename, fieldKey, letter) => {
    setMappings((prev) => ({
      ...prev,
      [filename]: { ...(prev[filename] || {}), [fieldKey]: letter },
    }));
  };

  // ── Step 2 → Step 3: preview with the (possibly adjusted) mapping ───────

  const runPreview = async (withWeather) => {
    // Process files **serially** so we don't burst a dozen parallel Lambda
    // invocations that all hammer Open-Meteo simultaneously. Each preview
    // makes one batched Open-Meteo call (~2-3s) thanks to the date-range
    // refactor, so even 10 files complete in ~25s — well under timeout.
    const mergedRecords = [];
    const fileSummaries = [];
    const fileLevelWeatherErrors = [];

    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      setPreviewProgress({ current: i + 1, total: files.length, filename: f.name });

      const formData = new FormData();
      formData.append('file', f);
      formData.append('lat', lat);
      formData.append('lon', lon);
      const mapping = mappings[f.name] || {};
      const cleaned = {};
      for (const [k, v] of Object.entries(mapping)) {
        if (v) cleaned[k] = v;
      }
      formData.append('column_map', JSON.stringify(cleaned));
      // The backend default is true; we send the flag explicitly so the
      // UI's "Skip weather" choice is honoured even on retry paths.
      formData.append('include_weather', withWeather ? 'true' : 'false');

      const data = await previewExcel(formData);

      fileSummaries.push({
        filename: f.name,
        year: data.year,
        month: data.month,
        total_sheets: data.total_sheets,
        record_count: data.records.length,
        weather_skipped: Boolean(data.weather_skipped),
        weather_error: data.weather_error || null,
      });
      if (data.weather_error) {
        fileLevelWeatherErrors.push(`${f.name}: ${data.weather_error}`);
      }
      for (const r of data.records) {
        mergedRecords.push({
          ...r,
          source_file: f.name,
          scoped_row_ref: scopedRowRef(f.name, r.row_ref),
        });
      }
    }

    return {
      aggregated: { records: mergedRecords, files: fileSummaries },
      weatherErrors: fileLevelWeatherErrors,
    };
  };

  const isTimeoutErr = (err) => {
    // axios timeouts surface as ECONNABORTED with no response, or a 502/503/504
    // when API Gateway gives up on the Lambda first.
    if (err.code === 'ECONNABORTED') return true;
    const status = err.response?.status;
    return status === 502 || status === 503 || status === 504;
  };

  const handlePreview = async () => {
    setIsPreviewing(true);
    setError(null);
    setWeatherTimeoutHint(false);
    setPreviewProgress({ current: 0, total: files.length, filename: '' });
    try {
      const { aggregated, weatherErrors } = await runPreview(!skipWeather);

      // Surface partial weather failures (Open-Meteo refused, but parse
      // succeeded) as a non-blocking warning rather than an error.
      if (weatherErrors.length > 0) {
        setError(
          `Weather data not available for some files — records imported without FDI estimates: ${weatherErrors.join('; ')}`,
        );
      }

      setPreviewData(aggregated);
      const validRefs = aggregated.records
        .filter((r) => r.is_valid)
        .map((r) => r.scoped_row_ref);
      setSelectedRows(new Set(validRefs));
      setStep('preview');
    } catch (err) {
      if (isTimeoutErr(err) && !skipWeather) {
        // Weather lookup blew the gateway timeout. Offer one-click retry
        // without weather rather than just dumping the error on the user.
        setWeatherTimeoutHint(true);
        setError(
          'Weather lookup timed out (Open-Meteo took too long). You can retry without weather data — records will still parse and import, just without peak-FDI estimates.',
        );
      } else {
        const detail = err.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.errors) {
          setError(`Parse errors: ${detail.errors.join('; ')}`);
        } else {
          setError(detail || err.message || 'Preview failed. Check the file format and try again.');
        }
      }
    } finally {
      setIsPreviewing(false);
      setPreviewProgress(null);
    }
  };

  const handleRetryWithoutWeather = async () => {
    setSkipWeather(true);
    setWeatherTimeoutHint(false);
    // Call directly with weather forced off, regardless of the toggle's
    // current React state (which only updates next render).
    setIsPreviewing(true);
    setError(null);
    setPreviewProgress({ current: 0, total: files.length, filename: '' });
    try {
      const { aggregated } = await runPreview(false);
      setPreviewData(aggregated);
      const validRefs = aggregated.records
        .filter((r) => r.is_valid)
        .map((r) => r.scoped_row_ref);
      setSelectedRows(new Set(validRefs));
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Preview failed.');
    } finally {
      setIsPreviewing(false);
      setPreviewProgress(null);
    }
  };

  // ── Step 3 → Step 4: confirm import ─────────────────────────────────────

  const handleConfirm = async () => {
    // Super-admins import "as" the currently-selected tenant from the tenant
    // switcher; the backend's `enforce_tenant_ownership` requires the id
    // explicitly (rejects with 400 "tenant_id is required"). Non-super-
    // admins have their own tenant forced server-side either way, so
    // passing the value through is safe for them too.
    const tenantId = currentTenant?.id ?? null;
    if (!tenantId) {
      setError(
        'Select an FPA from the tenant switcher (top-right) before importing — ' +
          'the records need to be attached to a specific FPA.',
      );
      return;
    }

    setIsConfirming(true);
    setError(null);
    try {
      const recordsToImport = previewData.records
        .filter((r) => selectedRows.has(r.scoped_row_ref))
        // eslint-disable-next-line no-unused-vars
        .map(({ source_file, scoped_row_ref, ...rest }) => rest);
      const data = await confirmExcelImport({
        records: recordsToImport,
        lat: parseFloat(lat),
        lon: parseFloat(lon),
        tenant_id: tenantId,
      });
      setImportResult(data);
      setStep('result');
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  // ── Reset / step navigation ─────────────────────────────────────────────

  const handleReset = () => {
    setFiles([]);
    setFilenameErrors({});
    setSniffs({});
    setMappings({});
    setPreviewData(null);
    setSelectedRows(new Set());
    setImportResult(null);
    setError(null);
    setShowErrors(false);
    setStep('select');
    const input = document.getElementById('excel-import-file');
    if (input) input.value = '';
  };

  const handleBackToMapping = () => {
    setPreviewData(null);
    setSelectedRows(new Set());
    setError(null);
    setStep('mapping');
  };

  const handleBackToSelect = () => {
    setSniffs({});
    setMappings({});
    setError(null);
    setStep('select');
  };

  // ── Selection helpers (preview step) ────────────────────────────────────

  const handleSelectAll = () => {
    if (!previewData) return;
    const validRefs = previewData.records
      .filter((r) => r.is_valid)
      .map((r) => r.scoped_row_ref);
    setSelectedRows(new Set(validRefs));
  };
  const handleDeselectAll = () => setSelectedRows(new Set());
  const handleRowToggle = (rowRef) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowRef)) next.delete(rowRef);
      else next.add(rowRef);
      return next;
    });
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const warningCount = previewData
    ? previewData.records.filter((r) => r.parse_warnings && r.parse_warnings.length > 0).length
    : 0;
  const invalidCount = previewData
    ? previewData.records.filter((r) => !r.is_valid).length
    : 0;
  const showSourceColumn = previewData && previewData.files.length > 1;

  // ── Step 4: result ──────────────────────────────────────────────────────

  if (step === 'result' && importResult) {
    return (
      <Box>
        <Alert severity={importResult.skipped_count === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
          Import completed — {importResult.imported_count} record
          {importResult.imported_count !== 1 ? 's' : ''} imported
          {importResult.skipped_count > 0 ? `, ${importResult.skipped_count} skipped` : ''}.
        </Alert>
        {importResult.errors && importResult.errors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              startIcon={showErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowErrors((v) => !v)}
            >
              {showErrors ? 'Hide' : 'Show'} {importResult.errors.length} error
              {importResult.errors.length !== 1 ? 's' : ''}
            </Button>
            <Collapse in={showErrors}>
              <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
                {importResult.errors.map((e, i) => (
                  <Typography key={i} variant="caption" display="block" color="error">
                    {e.row_ref}: {e.error}
                  </Typography>
                ))}
              </Paper>
            </Collapse>
          </Box>
        )}
        <Button variant="outlined" onClick={handleReset}>
          Import Another File
        </Button>
      </Box>
    );
  }

  // ── Step 3: preview table ───────────────────────────────────────────────

  if (step === 'preview' && previewData) {
    const { records, files: fileSummaries } = previewData;
    const monthsSummary = fileSummaries
      .map((f) => `${MONTH_NAMES[f.month]} ${f.year}`)
      .join(', ');
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={handleBackToMapping}>
            Back to mapping
          </Button>
          <Typography variant="subtitle1">
            Found <strong>{records.length}</strong> record{records.length !== 1 ? 's' : ''} across{' '}
            <strong>{fileSummaries.length}</strong> file{fileSummaries.length !== 1 ? 's' : ''} (
            {monthsSummary})
          </Typography>
        </Box>

        {invalidCount > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {invalidCount} record{invalidCount !== 1 ? 's' : ''} could not be parsed (missing date)
            and will not be importable.
          </Alert>
        )}
        {warningCount > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {warningCount} record{warningCount !== 1 ? 's have' : ' has'} non-critical parse warnings.
            Check the Warnings column.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button size="small" startIcon={<CheckBoxIcon />} onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" startIcon={<CheckBoxOutlineBlankIcon />} onClick={handleDeselectAll}>
            Deselect All
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center', ml: 1 }}>
            {selectedRows.size} selected
          </Typography>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {showSourceColumn && <TableCell>File</TableCell>}
                <TableCell>Row</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="right">Ha</TableCell>
                <TableCell align="right">People</TableCell>
                <TableCell>FDI (Peak)</TableCell>
                <TableCell>Peak Hour</TableCell>
                <TableCell>Warnings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => {
                const isSelected = selectedRows.has(record.scoped_row_ref);
                const chipProps = getFdiChipProps(record);
                return (
                  <TableRow
                    key={record.scoped_row_ref}
                    sx={{ opacity: record.is_valid ? 1 : 0.45 }}
                    hover={record.is_valid}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={!record.is_valid}
                        onChange={() => handleRowToggle(record.scoped_row_ref)}
                        size="small"
                      />
                    </TableCell>
                    {showSourceColumn && (
                      <TableCell>
                        <Tooltip title={record.source_file}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              maxWidth: 180,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              verticalAlign: 'middle',
                            }}
                          >
                            {record.source_file}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {record.row_ref}
                      </Typography>
                    </TableCell>
                    <TableCell>{record.start_date || '—'}</TableCell>
                    <TableCell>{record.suburb || '—'}</TableCell>
                    <TableCell align="right">
                      {record.area_size_hectares != null ? record.area_size_hectares.toFixed(1) : '—'}
                    </TableCell>
                    <TableCell align="right">{record.people_involved ?? '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" {...chipProps} />
                    </TableCell>
                    <TableCell>
                      {record.weather_preview?.peak_hour != null
                        ? `${String(record.weather_preview.peak_hour).padStart(2, '0')}:00`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {record.parse_warnings && record.parse_warnings.length > 0 ? (
                        <Typography variant="caption" color="warning.main">
                          {record.parse_warnings.join('; ')}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleReset} disabled={isConfirming}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={isConfirming ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handleConfirm}
            disabled={selectedRows.size === 0 || isConfirming}
          >
            {isConfirming
              ? 'Importing...'
              : `Confirm Import (${selectedRows.size} record${selectedRows.size !== 1 ? 's' : ''})`}
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Step 2: column mapping ──────────────────────────────────────────────

  if (step === 'mapping') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={handleBackToSelect}>
            Back
          </Button>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Confirm column mapping
          </Typography>
          <Chip
            size="small"
            label={`${files.length} file${files.length === 1 ? '' : 's'}`}
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We&apos;ve auto-detected which Excel columns map to each field. Review and adjust if
          needed, then preview the records.
        </Typography>

        {error && (
          <Alert
            severity={weatherTimeoutHint ? 'warning' : 'error'}
            sx={{ mb: 2 }}
            action={
              weatherTimeoutHint ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetryWithoutWeather}
                  disabled={isPreviewing}
                >
                  Retry without weather
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}

        {files.map((f) => (
          <ColumnMapper
            key={f.name}
            sniff={sniffs[f.name]}
            mapping={mappings[f.name] || {}}
            onMappingChange={(fieldKey, letter) =>
              handleMappingChange(f.name, fieldKey, letter)
            }
          />
        ))}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleReset}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={isPreviewing ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handlePreview}
            disabled={!allMappingsValid || isPreviewing}
          >
            {isPreviewing
              ? previewProgress && previewProgress.total > 0
                ? `Processing ${previewProgress.current}/${previewProgress.total}${skipWeather ? '' : ' (with weather)'}…`
                : skipWeather
                  ? `Parsing records… (${files.length} file${files.length !== 1 ? 's' : ''})`
                  : `Parsing & fetching weather… (${files.length} file${files.length !== 1 ? 's' : ''})`
              : 'Preview Records'}
          </Button>
          <FormControlLabel
            sx={{ ml: 1 }}
            control={
              <Switch
                size="small"
                checked={skipWeather}
                onChange={(e) => setSkipWeather(e.target.checked)}
                disabled={isPreviewing}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Skip weather (faster — no FDI estimates)
              </Typography>
            }
          />
        </Box>
        {!allMappingsValid && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            Every file must have at least the <strong>Date</strong> column mapped.
          </Typography>
        )}
      </Box>
    );
  }

  // ── Step 1: file pick + coordinates ─────────────────────────────────────

  const fpaCoordsAvailable = fpaLat != null && fpaLon != null;

  return (
    <Box>
      {!hasTenant && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No FPA selected — pick one from the tenant switcher (top-right) before
          importing. Records have to be attached to a specific FPA.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
        {/* File picker */}
        <Box>
          <input
            id="excel-import-file"
            type="file"
            accept=".xls,.xlsx"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <label htmlFor="excel-import-file">
            <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
              Choose Excel File{files.length === 1 ? '' : 's'}
            </Button>
          </label>
          {files.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {files.map((f) => {
                const err = filenameErrors[f.name];
                return (
                  <Box
                    key={f.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: err ? 'error.lighter' : 'action.hover',
                      border: err ? '1px solid' : 'none',
                      borderColor: 'error.main',
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                      <strong>{f.name}</strong>
                      {err && (
                        <Typography variant="caption" color="error" display="block">
                          {err}
                        </Typography>
                      )}
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => removeFile(f.name)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Remove
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            You can select multiple files. Both .xlsx and legacy .xls are supported. Each
            filename must end with YYYYMM (e.g. "Roodeplaat FPA MONTHLY FIRE STATISTICS 202412.xlsx").
          </Typography>
        </Box>

        {/* Coordinates */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="FPA Latitude"
              size="small"
              value={lat}
              onChange={handleLatChange}
              error={!!latError}
              helperText={latError || 'e.g. -25.7482'}
              sx={{ flex: 1 }}
            />
            <TextField
              label="FPA Longitude"
              size="small"
              value={lon}
              onChange={handleLonChange}
              error={!!lonError}
              helperText={lonError || 'e.g. 28.3167'}
              sx={{ flex: 1 }}
            />
          </Box>

          {fpaCoordsAvailable && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="text"
                startIcon={<MyLocationIcon />}
                onClick={handleUseFpaCoords}
                disabled={
                  coordsAutoFilled &&
                  lat === String(fpaLat) &&
                  lon === String(fpaLon)
                }
              >
                Use {fpaName ?? 'FPA'} coords
              </Button>
              {coordsAutoFilled &&
                lat === String(fpaLat) &&
                lon === String(fpaLon) && (
                  <Chip
                    size="small"
                    label={`Defaulted from ${fpaName ?? 'selected FPA'}`}
                    color="success"
                    variant="outlined"
                  />
                )}
            </Box>
          )}
          {!fpaCoordsAvailable && (
            <Typography variant="caption" color="text.secondary">
              Tip: set latitude/longitude on the selected FPA in Admin → FPA Areas and these
              will auto-fill next time.
            </Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          The FPA location is used to fetch historical weather data and estimate the peak Fire
          Danger Index (FDI) for each incident date.
        </Typography>

        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={isSniffing ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handleSniff}
            disabled={!isReadyToSniff}
          >
            {isSniffing
              ? previewProgress && previewProgress.total > 0
                ? `Reading ${previewProgress.current}/${previewProgress.total}…`
                : `Reading files… (${files.length})`
              : `Detect columns${files.length > 1 ? ` (${files.length} files)` : ''}`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
